/* Re-anchor fixed page chrome after iOS Safari has moved the viewport out from under it.
 *
 * THE BUG THIS EXISTS FOR. Generating on Home cuts to the Workspace. You reached the submit
 * control by scrolling, so Safari's URL bar is COLLAPSED at that moment; the cut resets scroll
 * to the top, Safari expands the URL bar again, and the visual viewport both shrinks and
 * shifts by about the bar's height. Fixed elements are not re-anchored when that happens —
 * they are re-anchored on a real scroll — so the nav stays painted for the viewport that
 * existed a frame ago and lands ~70px down the page, on top of the first row of cards.
 *
 * It is genuinely still `position: fixed`: scrolling moves the cards under it while it holds
 * its wrong place, and scrolling far enough to collapse the URL bar again snaps it home. That
 * behaviour is the whole diagnosis — a lost `fixed` would scroll away with the content, and a
 * bad containing block would never correct itself.
 *
 * WHY A DISPLAY TOGGLE. Taking the element out of flow and reading a layout property forces a
 * synchronous reflow of it, which is what makes WebKit recompute where a fixed element
 * belongs. It happens inside one frame with no paint in between, so nothing flashes. The
 * alternatives are worse: a 1px scroll nudge is a visible jump and does not work at scrollTop
 * 0, and anything that puts a `transform` on an ancestor would create a containing block and
 * cause a permanent version of the very bug being fixed.
 *
 * Opt-in by `data-fixed-chrome`, so this only ever touches elements that asked for it.
 */
export const reanchorFixedChrome = () => {
  document.querySelectorAll("[data-fixed-chrome]").forEach((el) => {
    const prev = el.style.display;
    el.style.display = "none";
    void el.offsetHeight; // reflow, with the element out of flow
    el.style.display = prev;
  });
};

/* Run `fn` once the visual viewport has stopped moving.
 *
 * The URL bar animates, so re-anchoring on the next frame re-anchors to a viewport that is
 * still in motion and fixes nothing. Three identical readings is the settle signal; the frame
 * cap keeps a viewport that never settles (a keyboard held open, a user scrolling throughout)
 * from leaving a rAF loop running behind the new page. Browsers without visualViewport do not
 * have this problem and go straight through.
 */
export const afterViewportSettles = (fn) => {
  const vv = window.visualViewport;
  if (!vv) return fn();

  let last = vv.height;
  let stable = 0;
  let frames = 0;

  const tick = () => {
    if (vv.height === last) stable += 1;
    else { stable = 0; last = vv.height; }

    if (stable >= 3 || frames++ > 60) return fn();
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

/* Force the PAGE to be re-rendered, not just re-measured.
 *
 * MEASURED, generating on a real iPhone (1.0.48):
 *
 *   +  0ms y=0  main=0   vt=0 vh=676 ih=676 d=0
 *   + 91ms y=67 main=-67 vt=0 vh=676 ih=676 d=0  ←vv-scroll
 *   + 97ms y=0  main=0   vt=0 vh=676 ih=676 d=0
 *   +931ms y=0  main=0   vt=0 vh=676 ih=676 d=0
 *
 * Every API agrees the page is at the top and stays there — scrollY 0, <main> flush at 0,
 * visualViewport offset 0, and ih-vh 0, which also rules out WebKit #297779 since the layout
 * and visual viewports are exactly the same size. And it still renders ~67px down.
 *
 * So layout is right and the COMPOSITOR is drawing something else. That is why nothing aimed
 * at the scroll position has worked: there is nothing wrong with the scroll position.
 * getBoundingClientRect cannot see it either, because it reads layout — which is why the
 * instrument agreed with the fixes rather than with the screen.
 *
 * A display toggle forces the element out of flow and back, which makes WebKit redo layout
 * AND paint rather than reusing what it has. This is the same technique that fixed the
 * stranded nav bar in 1.0.34, applied to the page container instead of to the nav; it is
 * within one frame with no paint in between, so nothing flashes.
 *
 * Deliberately narrow: only #main-content, only once per arrival, and only after the viewport
 * has stopped moving. It is a repaint, not a re-render — React state is untouched.
 */
export const forceRepaintPage = () => {
  const el = document.getElementById("main-content");
  if (!el) return;
  const prev = el.style.display;
  el.style.display = "none";
  void el.offsetHeight; // reflow, with the page out of flow
  el.style.display = prev;
};
